import { inject, Injectable, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { StateStorageService } from 'app/core/auth/state-storage.service';
import { Account } from 'app/core/auth/account.model';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Store } from '@ngrx/store';
import { accountFeature } from '../../store/account/account.state';
import { ApplicationConfigService } from '../../../core/config/application-config.service';
import { AccountService } from 'app/core/auth/account.service';

@Injectable({ providedIn: 'root' })
export class TravomateAccountService extends AccountService {
  private tmHttp = inject(HttpClient);
  private tmStateStorageService = inject(StateStorageService);
  private tmRouter = inject(Router);
  private appConfigService = inject(ApplicationConfigService);
  private store = inject(Store);

  identity(force?: boolean): Observable<Account | null> {
    return this.store.select(accountFeature.selectCurrentUser);
  }

  getAuthenticationState(): Observable<Account | null> {
    return this.store.select(accountFeature.selectCurrentUser);
  }

  trackCurrentAccount(): Signal<Account | null> {
    return this.store.selectSignal(accountFeature.selectCurrentUser);
  }

  hasAnyAuthority(authorities: string[] | string): boolean {
    if (!authorities || authorities.length === 0) {
      return true;
    }

    if (!Array.isArray(authorities)) {
      authorities = [authorities];
    }
    return this.store
      .selectSignal(accountFeature.selectAuthorities)()
      .some((authority: string) => authorities.includes(authority));
  }

  fetchCurrent(): Observable<Account> {
    return this.tmHttp.get<Account>(this.appConfigService.getEndpointFor('api/v1/user-accounts/current'));
  }

  restoreUrl(): void {
    const previousUrl = this.tmStateStorageService.getUrl();
    if (previousUrl) {
      this.tmStateStorageService.clearUrl();
      this.tmRouter.navigateByUrl(previousUrl);
    } else {
      this.tmRouter.navigate(['/']);
    }
  }

  getAuthorities(): Observable<string[]> {
    return from(fetchAuthSession()).pipe(
      map(session => {
        const accessToken = session.tokens?.accessToken;
        const groups = accessToken?.payload['cognito:groups'];
        return (
          groups
            ?.toString()
            .toUpperCase()
            .replace(/([^,]+)/g, 'ROLE_$1')
            .split(',') ?? []
        );
      }),
    );
  }
}
